import {
  DesignDto,
  DesignLogoDto,
  HeatingProductRefDto,
  MultiProductRefDto,
  DesignMeasurementLineDto,
} from "../responses/DesignDto";
import {
  Design,
  DesignLogo,
  HeatingProductRef,
  MultiProductRef,
} from "../../../core/models/entities/Design";
import { DesignMeasurementLine } from "../../../core/models/entities/DesignMeasurementLine";
import { Organization } from "../../../core/models/entities/Organization";
import { mapToOrganization } from "./mapOrganization";
import { mapToStyles, mapToStyleView } from "./mapStyle";
import { Price } from "../../../core/models/entities/Price";
import { mapToLogo, mapToProductRef } from "./mapLogo";
import { mapToApprovalGroup } from "./ApprovalMapper";
import { PriceDto } from "../responses/PriceDto";

export function mapToDesign(dto: DesignDto): Design {
  const mappedApproval = dto.approval ? mapToApprovalGroup(dto.approval) : null;

  // Use the approval type determined by the backend
  const approvalType = dto.approvalType;

  return {
    ...dto,
    updateAtUtc: new Date(dto.updateAtUtc),
    createdAtUtc: new Date(dto.createdAtUtc),
    // Drop designLogos whose nested logo is missing (partial payload): they
    // can neither be rendered nor mapped (mapToLogo reads logo.logoType), and
    // a throw here would flip the editor to the error view. Complete payloads
    // always carry the logo, so this filter is a no-op for them.
    designLogos: dto.designLogos
      ? dto.designLogos.filter((dl) => dl.logo).map(mapToDesignLogo)
      : [],
    customer: dto.customer
      ? mapToOrganization(dto.customer)
      : (undefined as unknown as Organization),
    style: dto.style ? mapToStyles(dto.style) : undefined,
    approval: mappedApproval,
    approvalType: approvalType,
    product: dto.product ? mapMultiProductRef(dto.product) : null,
    heating: dto.heating ? mapHeatingProductRef(dto.heating) : null,
    mainOrganizationId: dto.mainOrganizationId,
    measurementLines: dto.measurementLines
      ? dto.measurementLines.map(mapToDesignMeasurementLine)
      : [],
    // Customer-editable design note (flag designNoteEnabled). Additive — older API
    // payloads omit the fields and everything stays undefined.
    customerNote: dto.customerNote,
    customerNoteUpdatedAtUtc: dto.customerNoteUpdatedAtUtc
      ? new Date(dto.customerNoteUpdatedAtUtc)
      : undefined,
    customerNoteUpdatedByName: dto.customerNoteUpdatedByName,
    // Change Style only: how many logos the API moved to the first view.
    // Additive/optional — absent means none were moved.
    relocatedLogoCount: dto.relocatedLogoCount,
    viewImages: (Array.isArray(dto.viewImages)
      ? dto.viewImages
      : dto.viewImages
        ? [dto.viewImages]
        : undefined
    )?.map(mapToStyleView),
  };
}

export function mapMultiProductRef(dto: MultiProductRefDto): MultiProductRef {
  return {
    ...dto,
    // Null-guard like mapHeatingProductRef: a freshly cloned design's background
    // GetDesign can briefly return a product ref whose prices/products haven't been
    // computed yet (they arrive once the NAV price lookup settles). Without the guard
    // the .map() throws a TypeError inside react-query's `select`, which flips the
    // editor to the error view mid-session (server returned 200, so it never heals).
    totalPrices: dto.totalPrices ? dto.totalPrices.map(mapToPrice) : [],
    products: dto.products ? dto.products.map(mapToProductRef) : [],
  };
}

export function mapHeatingProductRef(
  dto: HeatingProductRefDto,
): HeatingProductRef {
  return {
    ...dto,
    prices: dto.prices ? dto.prices.map(mapToPrice) : undefined,
  };
}

export function mapToPrice(dto: PriceDto): Price {
  return { ...dto, currency: dto.currency ?? "DKK" };
}

function mapToDesignLogo(dto: DesignLogoDto): DesignLogo {
  return {
    ...dto,
    logo: mapToLogo(dto.logo),
    view: dto.view ? mapToStyleView(dto.view) : undefined,
    createdAtUtc: new Date(dto.createdAtUtc),
    updateAtUtc: new Date(dto.updateAtUtc),
  };
}

function mapToDesignMeasurementLine(
  dto: DesignMeasurementLineDto,
): DesignMeasurementLine {
  return {
    ...dto,
  };
}
